import { create } from 'zustand';
import { persist } from 'zustand/middleware';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const useStore = create(
  persist(
    (set, get) => ({
      // Language
      language: 'fr',
      setLanguage: (lang) => set({ language: lang }),

      // User Authentication
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
      
      setUser: (user, token) => {
        // Ajouter le role basé sur is_admin si pas déjà défini
        const userWithRole = {
          ...user,
          role: user.role || (user.is_admin ? 'admin' : 'customer')
        };
        localStorage.setItem('ebb_token', token);
        localStorage.setItem('ebb_user', JSON.stringify(userWithRole));
        set({ user: userWithRole, token, isAuthenticated: true });
      },
      
      // Fonction de connexion
      login: async (email, password) => {
        set({ isLoading: true });
        try {
          const response = await fetch(`${BACKEND_URL}/api/auth/login`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ email, password }),
          });
          
          const data = await response.json();
          
          if (response.ok && data.token) {
            // Ajouter le role basé sur is_admin
            const userWithRole = {
              ...data.user,
              role: data.user.is_admin ? 'admin' : 'customer'
            };
            
            localStorage.setItem('ebb_token', data.token);
            localStorage.setItem('ebb_user', JSON.stringify(userWithRole));
            set({ 
              user: userWithRole, 
              token: data.token, 
              isAuthenticated: true,
              isLoading: false 
            });
            return { success: true };
          } else {
            set({ isLoading: false });
            return { success: false, error: data.detail || 'Email ou mot de passe incorrect' };
          }
        } catch (error) {
          console.error('Login error:', error);
          set({ isLoading: false });
          return { success: false, error: 'Erreur de connexion au serveur' };
        }
      },
      
      logout: () => {
        localStorage.removeItem('ebb_token');
        localStorage.removeItem('ebb_user');
        set({ user: null, token: null, isAuthenticated: false, wishlist: [] });
      },
      
      initAuth: () => {
        const token = localStorage.getItem('ebb_token');
        const userStr = localStorage.getItem('ebb_user');
        if (token && userStr) {
          try {
            const user = JSON.parse(userStr);
            // S'assurer que le role est défini
            const userWithRole = {
              ...user,
              role: user.role || (user.is_admin ? 'admin' : 'customer')
            };
            set({ user: userWithRole, token, isAuthenticated: true });
          } catch {
            localStorage.removeItem('ebb_token');
            localStorage.removeItem('ebb_user');
          }
        }
      },

      // Cart
      cart: [],
      cartSessionId: null,
      
      initCartSession: () => {
        const sessionId = get().cartSessionId || `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        set({ cartSessionId: sessionId });
        return sessionId;
      },

      setCart: (items) => set({ cart: items }),

      addToCart: (item) => {
        const cart = get().cart;
        const existingIndex = cart.findIndex(
          (i) => i.product_id === item.product_id && i.size === item.size && i.color === item.color
        );

        if (existingIndex >= 0) {
          const newCart = [...cart];
          newCart[existingIndex].quantity += item.quantity;
          set({ cart: newCart });
        } else {
          set({ cart: [...cart, item] });
        }
      },

      updateCartItem: (productId, size, color, quantity) => {
        const cart = get().cart;
        if (quantity <= 0) {
          set({
            cart: cart.filter(
              (i) => !(i.product_id === productId && i.size === size && i.color === color)
            ),
          });
        } else {
          set({
            cart: cart.map((i) =>
              i.product_id === productId && i.size === size && i.color === color
                ? { ...i, quantity }
                : i
            ),
          });
        }
      },

      removeFromCart: (productId, size, color) => {
        set({
          cart: get().cart.filter(
            (i) => !(i.product_id === productId && i.size === size && i.color === color)
          ),
        });
      },

      clearCart: () => set({ cart: [] }),

      getCartTotal: () => {
        return get().cart.reduce((total, item) => total + (item.price || 0) * item.quantity, 0);
      },

      getCartCount: () => {
        return get().cart.reduce((count, item) => count + item.quantity, 0);
      },

      // Wishlist
      wishlist: [],
      setWishlist: (wishlist) => set({ wishlist }),
      
      addToWishlist: (productId) => {
        const wishlist = get().wishlist;
        if (!wishlist.includes(productId)) {
          set({ wishlist: [...wishlist, productId] });
        }
      },
      
      removeFromWishlistLocal: (productId) => {
        set({ wishlist: get().wishlist.filter((id) => id !== productId) });
      },
      
      isInWishlist: (productId) => {
        return get().wishlist.includes(productId);
      },

      // Products cache
      products: [],
      setProducts: (products) => set({ products }),

      // Site settings
      siteSettings: {
        freeShippingThreshold: 75,
        shippingCost: 5.95,
      },
      setSiteSettings: (settings) => set({ siteSettings: settings }),
    }),
    {
      name: 'ebb-shop-storage',
      partialize: (state) => ({
        language: state.language,
        cart: state.cart,
        cartSessionId: state.cartSessionId,
        wishlist: state.wishlist,
      }),
    }
  )
);

export default useStore;
